// PWA kill switch, layer 1 — AgDR-0269. At start the app reads
// `/pwa-control.json` (never cached: `no-store` here, excluded from the service
// worker, `no-cache` from the server). If `killSwitch` is true, or this build is
// older than `minVersion`, it unregisters every service worker of its scope,
// deletes every cache, then reloads once. A failed fetch does nothing.
// The worker is registered only after this check (`startPwa`), so a page
// loaded while the switch is on does not register it again.
// Layer 2, when this code is not in the stuck build: deploy a build with
// PWA_SELF_DESTROY=true (vite-plugin-pwa `selfDestroying`), then a normal one.

export interface PwaControl {
  killSwitch?: boolean;
  minVersion?: string;
}

const RELOAD_FLAG = 'pwa-control-reloaded';

/** Checks the control file, then registers the service worker unless it was killed. */
export async function startPwa(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  if (await checkPwaControl()) return;
  const { registerSW } = await import('virtual:pwa-register');
  registerSW({ immediate: true });
}

/** Returns true when the switch fired (workers and caches removed). */
export async function checkPwaControl(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  let control: PwaControl;
  try {
    const res = await fetch('/pwa-control.json', { cache: 'no-store' });
    if (!res.ok) return false;
    control = (await res.json()) as PwaControl;
  } catch {
    return false;
  }
  const version = import.meta.env.VITE_APP_VERSION;
  if (!shouldKill(control, version)) return false;

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((r) => r.unregister()));
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  try {
    if (sessionStorage.getItem(RELOAD_FLAG)) return true;
    sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    return true; // No session storage: no reload, to rule out a loop.
  }
  window.location.reload();
  return true;
}

export function shouldKill(control: PwaControl, version: string | undefined): boolean {
  if (control.killSwitch === true) return true;
  if (!control.minVersion || !version) return false;
  return compareSemver(version, control.minVersion) < 0;
}

/** Compares x.y.z (pre-release and build parts ignored); a non-numeric part counts as 0. */
export function compareSemver(a: string, b: string): number {
  const parts = (v: string) =>
    v
      .replace(/^v/, '')
      .split(/[-+]/)[0]!
      .split('.')
      .map((n) => Number.parseInt(n, 10) || 0);
  const [pa, pb] = [parts(a), parts(b)];
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}
